import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, ElementRef, inject, input, InputSignal, output, signal, viewChild } from '@angular/core';
import { DateFieldComponent, TimeFieldsComponent, MovilityFieldComponent, LocationFieldComponent, TripTypesFieldComponent, InterestsFieldComponent, TransportMeansFieldComponent, MaxDistanceFieldComponent } from "../../shared/components";
import { FormGroup } from '@angular/forms';
import Swiper, { Navigation, Pagination } from 'swiper';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RegisterUserFormService } from '../../services';
import { SLIDE_VALIDATION_FIELDS } from '../../config/constants';
import { GeolocationRequestService } from './services';
import { openCiSnackbar } from '@iurban-malaga/cicerone-ui';
  
@Component({
  selector: 'app-register-user-form-mobile',
  standalone: true,
  imports: [
    TranslateModule,
    DateFieldComponent,
    TimeFieldsComponent,
    MovilityFieldComponent,
    LocationFieldComponent,
    TripTypesFieldComponent,
    InterestsFieldComponent,
    TransportMeansFieldComponent,
    MaxDistanceFieldComponent,
],
  templateUrl: './register-user-form-mobile.component.html',
  styleUrls: ['./register-user-form-mobile.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class RegisterUserFormMobileComponent {
  public swiperRef = viewChild<ElementRef>('swiper');
  public form: InputSignal<FormGroup> = input.required<FormGroup>();
  
  public submitFormEmitter = output<void>();
  
  private _translateService = inject(TranslateService);
  private _registerUserFormService = inject(RegisterUserFormService);
  private _geolocationRequestService = inject(GeolocationRequestService);
  
  // Computed para controles de navegación
  protected canGoNext = computed(() => this.isCurrentSlideValid());
  protected canGoPrev = computed(() => this._currentSlideIndex() > 0); 
  protected isCurrentSlideValid = computed(() => {
    const currentSlide = this._currentSlideIndex();
    const form = this.form();
    
    if (!form) {
      console.warn('Form not available');
      return false;
    }
  
    switch(currentSlide) {
      case 0:
        // Slide de fechas
        const startDateControl = form.get('startDate');
        const endDateControl = form.get('endDate');
        const startDate = this._registerUserFormService.startDate();
        const endDate = this._registerUserFormService.endDate();
        
        const slide0Valid = Boolean(
          startDateControl?.valid && 
          endDateControl?.valid && 
          startDate && 
          endDate
        );
        
        if (!slide0Valid && this.isNextButtonClicked()) {
          this._openErrorFormSnackbar('Selecciona las fechas de inicio y fin del viaje');
        }

        return slide0Valid;
        
      case 1:
        // Slide de ubicación - SOLO validar ubicación
        const locationControl = form.get('startLocationAddress');
        const startLocation = this._registerUserFormService.startLocation();
        
        const hasControlValue = Boolean(locationControl?.value && locationControl.value.trim() !== '');
        const hasServiceLocation = Boolean(startLocation?.address && startLocation.address.trim() !== '');
        const isControlValid = Boolean(locationControl?.valid);
        
        // Para el slide 1, necesitamos que haya una ubicación válida
        // Si hay texto en el control pero no hay ubicación en el servicio, 
        // significa que el usuario escribió algo pero no seleccionó una ubicación del autocomplete
        const slide1Valid = hasControlValue && hasServiceLocation && isControlValid;

        if (!slide1Valid && this.isNextButtonClicked()) {
          this._openErrorFormSnackbar('Selecciona un punto de inicio válido');
        }

        return slide1Valid;
        
      case 2:
        // Slide de tipo de viaje
        const tripTypeControl = form.get('tripType');
        const tripTypeId = this._registerUserFormService.tripTypeId();
        
        const slide2Valid = Boolean(
          tripTypeControl?.valid && 
          tripTypeId
        );

        if (!slide2Valid && this.isNextButtonClicked()) {
          this._openErrorFormSnackbar('Selecciona un tipo de viajero');
        }

        return slide2Valid;
        
      case 3:
        // Slide de intereses
        const interestsControl = form.get('interestIdList');
        const interestIdList = this._registerUserFormService.interests();
        
        const slide3Valid = Boolean(
          interestsControl?.valid && 
          interestIdList && 
          interestIdList.length > 0
        );
        
        if (!slide3Valid && this.isNextButtonClicked()) {
          this._openErrorFormSnackbar('Selecciona al menos tres intereses');
        }

        return slide3Valid;
        
      case 4:
        // Slide de medios de transporte y distancia máxima a pie
        const transportMeansControl = form.get('transportMeans');
        const maxDistanceControl = form.get('maxDistanceWalking');
        const transportMeans = this._registerUserFormService.transportMeans();
        const maxDistanceWalking = this._registerUserFormService.maxDistanceWalking();
        
        const slide4Valid = Boolean(
          transportMeansControl?.valid && 
          maxDistanceControl?.valid && 
          transportMeans && 
          transportMeans.length > 0 &&
          maxDistanceWalking !== null && 
          maxDistanceWalking !== undefined
        );
        
        if (!slide4Valid && this.isLastSlideValidationActive()) {
          this._openErrorFormSnackbar('Selecciona al menos un medio de transporte');
        }

        return slide4Valid;
        
      default:
        console.warn('Unknown slide, returning false');
        return false;
    }
  });

  protected isLastSlide = computed(() => {
    const currentSlide = this._currentSlideIndex();
    const totalSlides = this._swiperInstance ? this._swiperInstance.slides.length : 0; 
    const isLast = currentSlide >= totalSlides - 1;  
    return isLast;
  });
  
  private _domReady = signal(false);
  private _currentSlideIndex = signal(0);
  private _swiperInstance!: Swiper;  
  protected isNextButtonClicked = signal(false);
  protected isLastSlideValidationActive = signal(false);
  
  setSWiper = effect(() => {
    if(!this._domReady() || !this.swiperRef()) return;
    
    const swiperEl = this.swiperRef().nativeElement;
    if(!swiperEl) return;

    if (this._swiperInstance) {
      this._swiperInstance.destroy(true, true);
    }
    
    const swiperConfig = {
      modules: [Navigation, Pagination],
      slidesPerView: 1,
      allowTouchMove: false,
      loop: false,
      navigation: false,
      
      pagination: {
        el: '.swiper-pagination',
        clickable: false,
        type: 'bullets' as const,
      },
      
      on: {
        slideChange: (swiper: Swiper) => {
          this._currentSlideIndex.set(swiper.activeIndex);
        }
      }
    };
    
    this._swiperInstance = new Swiper(swiperEl, swiperConfig);
  }, { allowSignalWrites: true });
  
  ngAfterViewInit(): void {
    this._domReady.set(true);
  }
  
  // Métodos de navegación manual
  public goToNextSlide(): void {
    if (!this._swiperInstance) return;

    this.isNextButtonClicked.set(true);

    // Verificar validación
    if (!this.canGoNext()) {
      this._markCurrentSlideFieldsAsTouched();
      return;
    }
    
    if (this.isLastSlide()) {
      this.handleGeolocationRequest();
      return;
    }
      
    this._swiperInstance.slideNext();
    this.isNextButtonClicked.set(false);
  }
  

  private handleGeolocationRequest(): void {
    const geolocationControl = this.form().get('geolocation');
    
    if (geolocationControl) {
      geolocationControl.setValue(true);
      geolocationControl.markAsTouched();
    }
  }
  
  public goToPrevSlide(): void {
    if (!this._swiperInstance) return;

    this.isNextButtonClicked.set(false);
    this.isLastSlideValidationActive.set(false);
  
    if (this.canGoPrev()) {
      this._swiperInstance.slidePrev();
    }
  }
  
  // Marcar campos como touched para mostrar errores
  private _markCurrentSlideFieldsAsTouched(): void {
    const currentSlide = this._currentSlideIndex();
    const fields = SLIDE_VALIDATION_FIELDS[currentSlide];
    const form = this.form();
    
    if (fields && form) {
      fields.forEach(fieldName => {
        const control = form.get(fieldName);
        if (control) {
          control.markAsTouched();
          control.updateValueAndValidity();
        }
      });
    }
  }
  
  handleLastSlideClick(): void {   
    this.isLastSlideValidationActive.set(true);
    
    // Verificar si el último slide es válido antes de proceder
    if (!this.isCurrentSlideValid()) {
      this._markCurrentSlideFieldsAsTouched();
      return;
    }
    
    this._geolocationRequestService.setIsGeolocationView(true);
    this.submitFormEmitter.emit();
  }
  
  private _openErrorFormSnackbar(message: string) {
    openCiSnackbar({
      content: this._translateService.instant(message),
      status: 'error',
      'icon-name': 'error',
      'horizontal-position': 'end',
      'vertical-position': 'bottom',
    })
  } 
  
  ngOnDestroy(): void {
    if (this._swiperInstance) {
      this._swiperInstance.destroy(true, true);
    }
  }
}